<script>
import { GlPopover, GlLink } from '@gitlab/ui';
import { __, s__, sprintf } from '~/locale';
import { REACHABILITY_TEXT } from './constants';

let popoverId = 0;

export default {
  components: {
    GlPopover,
    GlLink,
  },
  props: {
    /**
     * Not required because SAST vulnerabilities don't have an EPSS score system.
     *
     * {
     *   // This is the KEV value, it tells whether the exploit is known or not.
     *   "isKnownExploit": Boolean,
     *
     *   // Likelihood of vulnerability being exploited in the next 30 days (value between 0 and 1)
     *   "epssScore": Float
     * }
     */
    cveEnrichment: {
      type: Object,
      required: false,
      default: undefined,
    },
    cvss: {
      type: Array,
      required: false,
      default: () => [],
    },
    reachability: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  computed: {
    popoverId() {
      return `security-score-${popoverId}`;
    },
    isKnownExploit() {
      return this.cveEnrichment.isKnownExploit;
    },
    cvssText() {
      return sprintf(this.$options.i18n.cvss, { version: this.cvss?.[0]?.version });
    },
    cvssOverallScore() {
      // Product decision to use overallScore: https://gitlab.com/gitlab-org/gitlab/-/issues/497388#note_2247367803
      return this.cvss?.[0]?.overallScore;
    },
    cveEnrichmentPopoverText() {
      // Product decision to always use +2 more: https://gitlab.com/gitlab-org/gitlab/-/merge_requests/173317#note_2251105162
      return sprintf(__('+%{count} more'), { count: 2 });
    },
    hasCveEnrichment() {
      return Boolean(this.cveEnrichment);
    },
    epssScore() {
      return Math.round((this.cveEnrichment.epssScore || 0) * 100);
    },
    reachableText() {
      return REACHABILITY_TEXT[this.reachability];
    },
    hasReachability() {
      return Boolean(this.reachability);
    },
  },
  created() {
    popoverId += 1;
  },
  i18n: {
    cvss: s__('SecurityReports|CVSS v%{version}:'),
    epss: s__('SecurityReports|EPSS:'),
    kev: s__('SecurityReports|KEV:'),
    reachable: s__('SecurityReports|Reachable:'),
  },
};
</script>
<template>
  <div v-if="cvssOverallScore" :id="popoverId" class="gl-mt-3 gl-text-secondary">
    <div>
      <p class="gl-mb-1">{{ cvssText }} {{ cvssOverallScore }}</p>
      <p v-if="hasCveEnrichment" class="gl-mb-0">
        <gl-link href="#">{{ cveEnrichmentPopoverText }}</gl-link>
      </p>
    </div>
    <gl-popover
      v-if="hasCveEnrichment"
      triggers="hover focus"
      placement="bottom"
      :show-close-button="false"
      :css-classes="['gl-w-25']"
      :target="popoverId"
    >
      <p class="gl-mb-1">
        {{ $options.i18n.epss }} <strong>{{ epssScore }}%</strong>
      </p>
      <p class="gl-mb-1">
        {{ $options.i18n.kev }}
        <strong>{{ isKnownExploit ? __('Yes') : __('No') }}</strong>
      </p>
      <p v-if="hasReachability" class="gl-mb-1">
        {{ $options.i18n.reachable }}
        <strong>{{ reachableText }}</strong>
      </p>
    </gl-popover>
  </div>
</template>
